import {
  faBoxOpen,
  faClockRotateLeft,
  faClose,
  faMoneyBillWave,
  faMotorcycle,
  faReceipt,
  faRoute,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { CourierFinancialDeliveryItem } from "../../../Objects/DeliveryEntity/CourierFinancialDeliveryItem"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"

interface CourierDeliveriesStatementPupopProps {
  title: string

  description: string

  counterpartName: string

  deliveries: CourierFinancialDeliveryItem[]

  onClose: () => void
}

export const CourierDeliveriesStatementPupop = ({
  title,
  description,
  counterpartName,
  deliveries,
  onClose,
}: CourierDeliveriesStatementPupopProps) => {
  const sortedDeliveries = [...deliveries].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())

  const totalEligibleAmount = sortedDeliveries.reduce((sum, item) => sum + item.courierAmount, 0)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faMotorcycle}
              boxShadow="none"
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.delivery.pupop.courierDeliveriesStatementPupop.button.onClose"
                />
              }
            />

            <div className="space-y-4 overflow-y-auto p-2">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-slate-700">
                  <div className="flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-orange-600 shadow-sm">
                      <FontAwesomeIcon icon={faRoute} />
                    </div>

                    <div>
                      <p className="font-bold text-slate-900">{counterpartName}</p>

                      <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">
                        Extrato detalhado de entregas
                      </p>
                    </div>
                  </div>

                  <p className="mt-1">{description}</p>
                </div>
              </Animate>

              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  {
                    label: "Total de entregas",
                    icon: faMotorcycle,
                    value: String(sortedDeliveries.length),
                  },
                  {
                    label: "Valor elegível",
                    icon: faMoneyBillWave,
                    value: totalEligibleAmount.toMoney(),
                  },
                  {
                    label: "Última atualização",
                    icon: faClockRotateLeft,
                    value: sortedDeliveries[0]?.updatedAt?.toLocaleDateTimeOrDash() ?? "-",
                  },
                ].map((item, index) => (
                  <Animate key={item.label} type={AnimationTypes.fadeInTraslate} duration={`${1.2 + index * 0.2}s`}>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                          <FontAwesomeIcon icon={item.icon} />
                        </div>

                        <p className="text-xs uppercase text-slate-500">{item.label}</p>
                      </div>

                      <strong className="mt-1 block text-lg text-slate-900">{item.value}</strong>
                    </div>
                  </Animate>
                ))}
              </div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="space-y-3">
                  <div className="divide-y divide-slate-200">
                    {sortedDeliveries.length === 0 && (
                      <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                        <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <FontAwesomeIcon icon={faBoxOpen} />
                          </div>
                          Nenhuma entrega encontrada para o período filtrado.
                        </div>
                      </Animate>
                    )}

                    {sortedDeliveries.map((delivery, index) => (
                      <Animate
                        key={`${delivery.deliveryId}-${delivery.orderId}-${index}`}
                        type={AnimationTypes.fadeInTraslate}
                        duration={`${1.5 + index * 0.3}s`}
                      >
                        <div className="rounded-xl mt-2 border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                          <div className="flex flex-col gap-3 border-b border-slate-100 pb-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wide text-orange-600">
                                Entrega {index + 1} do período
                              </p>

                              <h4 className="text-base font-bold text-slate-900">
                                Entrega #{delivery.deliveryId} vinculada ao pedido #{delivery.orderId}
                              </h4>
                            </div>

                            <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-left lg:min-w-[210px]">
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="text-orange-600" />

                                <p className="text-xs uppercase text-slate-500">Valor elegível do entregador</p>
                              </div>

                              <strong className="mt-1 block text-lg text-slate-900">
                                {delivery.courierAmount.toMoney()}
                              </strong>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <div className="rounded-lg bg-slate-50 p-3">
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faMotorcycle} className="text-orange-500" />

                                <p className="text-xs uppercase text-slate-500">ID da entrega</p>
                              </div>

                              <strong className="text-slate-900">#{delivery.deliveryId}</strong>
                            </div>

                            <div className="rounded-lg bg-slate-50 p-3">
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faReceipt} className="text-orange-500" />

                                <p className="text-xs uppercase text-slate-500">ID do pedido</p>
                              </div>

                              <strong className="text-slate-900">#{delivery.orderId}</strong>
                            </div>

                            <div className="rounded-lg bg-slate-50 p-3">
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faClockRotateLeft} className="text-orange-500" />

                                <p className="text-xs uppercase text-slate-500">Última atualização</p>
                              </div>

                              <strong className="text-slate-900">{delivery.updatedAt.toLocaleDateTimeOrDash()}</strong>
                            </div>

                            <div className="rounded-lg bg-slate-50 p-3">
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faRoute} className="text-orange-500" />

                                <p className="text-xs uppercase text-slate-500">Conferência rápida</p>
                              </div>

                              <strong className="text-slate-900">
                                Entrega #{delivery.deliveryId} / Pedido #{delivery.orderId}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </Animate>
                    ))}
                  </div>
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
